import { safeArray } from "@/helpers/functions/ArrayHelpers";
import {
  getMunicipalitiesByState,
  getStatesByRegion,
  normalizeMunicipalityValue,
  normalizeStateValue,
} from "./territoryCatalog";

export const COVERAGE_STATUS = Object.freeze({
  EMPTY: "empty",
  PARTIAL: "partial",
  COMPLETE: "complete",
});

export const EMPTY_COVERAGE = Object.freeze({
  selected: 0,
  total: 0,
  status: COVERAGE_STATUS.EMPTY,
});

function getCoverageStatus(selected, total) {
  if (total > 0 && selected === total) return COVERAGE_STATUS.COMPLETE;
  if (selected > 0) return COVERAGE_STATUS.PARTIAL;

  return COVERAGE_STATUS.EMPTY;
}

function hasState(selection, stateValue) {
  const normalizedValue = normalizeStateValue(stateValue);

  return safeArray(selection.estados).some(
    (state) => normalizeStateValue(state) === normalizedValue
  );
}

function hasMunicipality(selection, municipalityValue) {
  const normalizedValue = normalizeMunicipalityValue(municipalityValue);

  return safeArray(selection.municipios).some(
    (municipality) =>
      normalizeMunicipalityValue(municipality) === normalizedValue
  );
}

function addState(selection, stateValue) {
  const normalizedValue = normalizeStateValue(stateValue);
  if (!normalizedValue || hasState(selection, normalizedValue)) return;

  selection.estados.push({ sigla: normalizedValue });
}

function addMunicipality(selection, municipalityValue) {
  const normalizedValue = normalizeMunicipalityValue(municipalityValue);

  if (normalizedValue === null || hasMunicipality(selection, normalizedValue)) {
    return;
  }

  selection.municipios.push({ id: normalizedValue });
}

function removeState(selection, stateValue) {
  const normalizedValue = normalizeStateValue(stateValue);

  selection.estados = selection.estados.filter(
    (state) => normalizeStateValue(state) !== normalizedValue
  );
}

function removeMunicipality(selection, municipalityValue) {
  const normalizedValue = normalizeMunicipalityValue(municipalityValue);

  selection.municipios = selection.municipios.filter(
    (municipality) =>
      normalizeMunicipalityValue(municipality) !== normalizedValue
  );
}

function removeMunicipalitiesByState(selection, stateValue, catalog) {
  const normalizedValue = normalizeStateValue(stateValue);

  selection.municipios = selection.municipios.filter((municipality) => {
    const municipalityValue = normalizeMunicipalityValue(municipality);
    const mappedMunicipality = catalog.municipios.get(
      String(municipalityValue)
    );

    return mappedMunicipality?.uf !== normalizedValue;
  });
}

function removeMunicipalitiesByRegion(selection, regionValue, catalog) {
  const normalizedValue = String(regionValue);

  selection.municipios = selection.municipios.filter((municipality) => {
    const municipalityValue = normalizeMunicipalityValue(municipality);
    const mappedMunicipality = catalog.municipios.get(
      String(municipalityValue)
    );

    return mappedMunicipality?.regiao !== normalizedValue;
  });
}

function hasEveryRegionState(selection, regionValue, catalog) {
  const states = getStatesByRegion(catalog, regionValue);
  return (
    states.length > 0 && states.every((state) => hasState(selection, state))
  );
}

function toggleRegion(selection, regionValue, catalog) {
  const nextSelection = normalizeTerritorySelection(selection);
  const regionStates = getStatesByRegion(catalog, regionValue);
  const coverage = createTerritoryCoverage(nextSelection, catalog);
  const regionCoverage = getTerritoryCoverage(coverage, "regiao", regionValue);
  const isSelected =
    regionCoverage.status === COVERAGE_STATUS.COMPLETE ||
    hasEveryRegionState(nextSelection, regionValue, catalog);

  if (isSelected) {
    for (const state of regionStates) {
      removeState(nextSelection, state.sigla);
    }

    removeMunicipalitiesByRegion(nextSelection, regionValue, catalog);
    return compactTerritorySelection(nextSelection, catalog);
  }

  removeMunicipalitiesByRegion(nextSelection, regionValue, catalog);

  for (const state of regionStates) {
    addState(nextSelection, state.sigla);
  }

  return compactTerritorySelection(nextSelection, catalog);
}

function toggleState(selection, stateValue, catalog) {
  const normalizedValue = normalizeStateValue(stateValue);
  const mappedState = catalog.estados.get(String(normalizedValue));
  if (!mappedState) return normalizeTerritorySelection(selection);

  const nextSelection = normalizeTerritorySelection(selection);
  const coverage = createTerritoryCoverage(nextSelection, catalog);
  const stateCoverage = getTerritoryCoverage(
    coverage,
    "estado",
    normalizedValue
  );
  const isSelected =
    hasState(nextSelection, normalizedValue) ||
    stateCoverage.status === COVERAGE_STATUS.COMPLETE;

  if (isSelected) {
    removeState(nextSelection, normalizedValue);
    removeMunicipalitiesByState(nextSelection, normalizedValue, catalog);
    return compactTerritorySelection(nextSelection, catalog);
  }

  removeMunicipalitiesByState(nextSelection, normalizedValue, catalog);
  addState(nextSelection, normalizedValue);

  return compactTerritorySelection(nextSelection, catalog);
}

function toggleMunicipality(selection, municipalityValue, catalog) {
  const normalizedValue = normalizeMunicipalityValue(municipalityValue);
  const municipality = catalog.municipios.get(String(normalizedValue));
  if (!municipality) return normalizeTerritorySelection(selection);

  const nextSelection = normalizeTerritorySelection(selection);

  if (hasState(nextSelection, municipality.uf)) {
    const stateMunicipalities = getMunicipalitiesByState(
      catalog,
      municipality.uf
    );

    removeState(nextSelection, municipality.uf);
    removeMunicipalitiesByState(nextSelection, municipality.uf, catalog);

    for (const siblingMunicipality of stateMunicipalities) {
      if (siblingMunicipality.id === normalizedValue) continue;
      addMunicipality(nextSelection, siblingMunicipality.id);
    }

    return compactTerritorySelection(nextSelection, catalog);
  }

  if (hasMunicipality(nextSelection, normalizedValue)) {
    removeMunicipality(nextSelection, normalizedValue);
  } else {
    addMunicipality(nextSelection, normalizedValue);
  }

  return compactTerritorySelection(nextSelection, catalog);
}

const TOGGLE_BY_LEVEL = Object.freeze({
  regiao: toggleRegion,
  estado: toggleState,
  municipio: toggleMunicipality,
});

export function emptyTerritorySelection() {
  return {
    estados: [],
    municipios: [],
  };
}

export function normalizeTerritorySelection(value) {
  const states = new Map();
  const municipalities = new Map();

  for (const state of safeArray(value?.estados)) {
    const stateValue = normalizeStateValue(state);
    if (stateValue) states.set(stateValue, { sigla: stateValue });
  }

  for (const municipality of safeArray(value?.municipios)) {
    const municipalityValue = normalizeMunicipalityValue(municipality);

    if (municipalityValue !== null) {
      municipalities.set(String(municipalityValue), {
        id: municipalityValue,
      });
    }
  }

  return {
    estados: Array.from(states.values()),
    municipios: Array.from(municipalities.values()),
  };
}

export function compactTerritorySelection(value, catalog) {
  const selection = normalizeTerritorySelection(value);

  for (const state of catalog.estados.values()) {
    if (hasState(selection, state.sigla)) {
      removeMunicipalitiesByState(selection, state.sigla, catalog);
      continue;
    }

    const municipalities = getMunicipalitiesByState(catalog, state.sigla);
    const hasAllMunicipalities =
      municipalities.length > 0 &&
      municipalities.every((municipality) =>
        hasMunicipality(selection, municipality.id)
      );

    if (!hasAllMunicipalities) continue;

    removeMunicipalitiesByState(selection, state.sigla, catalog);
    addState(selection, state.sigla);
  }

  return selection;
}

export function createTerritoryCoverage(value, catalog) {
  const selection = normalizeTerritorySelection(value);
  const selectedStateValues = new Set(
    selection.estados.map((state) => state.sigla)
  );
  const explicitMunicipalityValues = new Set(
    selection.municipios.map((municipality) => municipality.id)
  );
  const effectiveMunicipalityValues = new Set(explicitMunicipalityValues);
  const stateCoverage = new Map();
  const regionCoverage = new Map();

  for (const municipality of catalog.municipios.values()) {
    if (selectedStateValues.has(municipality.uf)) {
      effectiveMunicipalityValues.add(municipality.id);
    }
  }

  for (const state of catalog.estados.values()) {
    const municipalities = getMunicipalitiesByState(catalog, state.sigla);
    const selected = municipalities.filter((municipality) =>
      effectiveMunicipalityValues.has(municipality.id)
    ).length;
    const total = municipalities.length;

    stateCoverage.set(state.sigla, {
      selected,
      total,
      status: getCoverageStatus(selected, total),
    });
  }

  for (const region of catalog.regioes.values()) {
    const states = getStatesByRegion(catalog, region.regiao);
    const totals = states.reduce(
      (result, state) => {
        const coverage = stateCoverage.get(state.sigla) || EMPTY_COVERAGE;

        result.selected += coverage.selected;
        result.total += coverage.total;
        return result;
      },
      { selected: 0, total: 0 }
    );

    regionCoverage.set(region.regiao, {
      ...totals,
      status: getCoverageStatus(totals.selected, totals.total),
    });
  }

  return {
    selectedStateValues,
    explicitMunicipalityValues,
    effectiveMunicipalityValues,
    stateCoverage,
    regionCoverage,
  };
}

export function getTerritoryCoverage(coverage, level, value) {
  if (level === "regiao") {
    return coverage.regionCoverage.get(String(value)) || EMPTY_COVERAGE;
  }

  if (level === "estado") {
    const stateValue = normalizeStateValue(value);
    return coverage.stateCoverage.get(String(stateValue)) || EMPTY_COVERAGE;
  }

  return EMPTY_COVERAGE;
}

export function isInheritedMunicipalitySelection(
  selection,
  municipalityValue,
  catalog
) {
  const normalizedValue = normalizeMunicipalityValue(municipalityValue);
  const municipality = catalog.municipios.get(String(normalizedValue));

  return !!municipality && hasState(selection, municipality.uf);
}

export function isTerritorySelected(
  level,
  value,
  selection,
  catalog,
  coverage
) {
  if (level === "regiao") {
    const regionCoverage = getTerritoryCoverage(coverage, level, value);

    return (
      regionCoverage.status === COVERAGE_STATUS.COMPLETE ||
      hasEveryRegionState(selection, value, catalog)
    );
  }

  if (level === "estado") {
    const stateCoverage = getTerritoryCoverage(coverage, level, value);

    return (
      stateCoverage.status === COVERAGE_STATUS.COMPLETE ||
      hasState(selection, value)
    );
  }

  const municipalityValue = normalizeMunicipalityValue(value);
  return coverage.effectiveMunicipalityValues.has(municipalityValue);
}

export function toggleTerritorySelection(level, value, selection, catalog) {
  const handler = TOGGLE_BY_LEVEL[level];
  if (!handler) return compactTerritorySelection(selection, catalog);

  return handler(selection, value, catalog);
}

export function removeTerritorySelection(level, value, selection, catalog) {
  const nextSelection = normalizeTerritorySelection(selection);

  if (level === "regiao") {
    for (const state of getStatesByRegion(catalog, value)) {
      removeState(nextSelection, state.sigla);
    }

    removeMunicipalitiesByRegion(nextSelection, value, catalog);
    return compactTerritorySelection(nextSelection, catalog);
  }

  if (level === "estado") {
    removeState(nextSelection, value);
    removeMunicipalitiesByState(nextSelection, value, catalog);
    return compactTerritorySelection(nextSelection, catalog);
  }

  removeMunicipality(nextSelection, value);
  return compactTerritorySelection(nextSelection, catalog);
}
